import {
  Avatar,
  Box,
  Flex,
  HStack,
  Link,
  Spacer,
  Stack,
  Text,
  Icon,
} from "@chakra-ui/react";
import { EmailIcon, PhoneIcon } from "@chakra-ui/icons";
import type { MatrimonyConnectionProfile } from "src/types/matrimonyConnections.types";
import { fetchAuthedImageObjectUrl } from "src/utils/authImage";
import React from "react";
import { Briefcase } from "lucide-react";

type Props = {
  profile: MatrimonyConnectionProfile;
  onViewProfile?: (id: string | number) => void;
};

const formatConnectedSince = (iso?: string | null) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

export default function ConnectionCard({ profile, onViewProfile }: Props) {
  const [authedPhotoUrl, setAuthedPhotoUrl] = React.useState<string>("");

  React.useEffect(() => {
    const fileName = String((profile as any)?.profilePhotoFileName || "");
    if (!fileName) {
      setAuthedPhotoUrl("");
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const url = await fetchAuthedImageObjectUrl(fileName);
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        setAuthedPhotoUrl((prev) => {
          if (prev && prev !== url) {
            try {
              URL.revokeObjectURL(prev);
            } catch {
              // ignore
            }
          }
          return url;
        });
      } catch {
        setAuthedPhotoUrl("");
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(profile as any)?.profilePhotoFileName]);

  React.useEffect(() => {
    return () => {
      if (authedPhotoUrl) {
        try {
          URL.revokeObjectURL(authedPhotoUrl);
        } catch {
          // ignore
        }
      }
    };
  }, [authedPhotoUrl]);

  const subtitleBits = [
    profile.connectedSince ? `Connected since ${formatConnectedSince(profile.connectedSince)}` : null,
    profile.age != null ? `${profile.age} yrs` : null,
    profile.height ? profile.height : null,
  ].filter(Boolean);

  const jobLine = [profile.occupation, profile.employerName].filter(Boolean).join(", ");
  const locationLine = [profile.community, profile.city].filter(Boolean).join(", ");

  return (
    <Box
      bg="#FFFFFF"
      border="1px solid rgba(108, 117, 125, 0.18)"
      borderRadius="16px"
      p={{ base: 4, md: 5 }}
    >
      <Flex gap={4} align={{ base: "flex-start", md: "center" }} direction={{ base: "column", md: "row" }}>
        <HStack spacing={4} align="flex-start" flex="1" minW={0}>
          <Box position="relative">
            <Avatar
              name={profile.fullName}
              src={authedPhotoUrl || profile.profilePhoto || undefined}
              size={{ base: "md", md: "lg" }}
            />
          </Box>

          <Stack spacing={1} minW={0} flex="1">
            <HStack spacing={2} minW={0} align="center">
              <Text fontWeight="700" color="#1F0812" noOfLines={1}>
                {profile.fullName}
              </Text>
            </HStack>

            {subtitleBits.length ? (
              <Text fontSize="sm" color="#6C757D" noOfLines={1}>
                {subtitleBits.join(" • ")}
              </Text>
            ) : null}

            {jobLine ? (
              <HStack spacing={2} minW={0}>
                <Icon as={Briefcase} size={3} />
                <Text fontSize="sm" color="#1F0812" noOfLines={1}>
                  {jobLine}
                </Text>
              </HStack>
            ) : null}

            {locationLine ? (
              <HStack spacing={2} minW={0}>
                <Text fontSize="sm" color="#6C757D" noOfLines={1}>
                  {locationLine}
                </Text>
              </HStack>
            ) : null}
          </Stack>
        </HStack>

        <Spacer />

        <Stack spacing={2} w={{ base: "full", md: "auto" }} align={{ base: "stretch", md: "flex-end" }}>
          <Stack spacing={2} align={{ base: "stretch", md: "flex-end" }}>
            <Flex
              align="center"
              justify={{ base: "flex-start", md: "flex-end" }}
              bg="#4A2C2A"
              color="#FFFFFF"
              borderRadius="999px"
              px={4}
              py={2}
              fontSize="sm"
              fontWeight={600}
              as={profile.mobileNo ? "a" : undefined}
              href={profile.mobileNo ? `tel:${profile.mobileNo}` : undefined}
              opacity={profile.mobileNo ? 1 : 0.5}
              pointerEvents={profile.mobileNo ? "auto" : "none"}
              gap={2}
              w={{ base: "full", md: "auto" }}
            >
              <Icon as={PhoneIcon} boxSize="14px" />
              <Text as="span" noOfLines={1}>
                {profile.mobileNo || "No phone"}
              </Text>
            </Flex>

            <Flex
              align="center"
              justify={{ base: "flex-start", md: "flex-end" }}
              bg="#FFFFFF"
              border="1px solid rgba(74, 44, 42, 0.25)"
              color="#4A2C2A"
              borderRadius="999px"
              px={4}
              py={2}
              fontSize="sm"
              fontWeight={600}
              as={profile.email ? "a" : undefined}
              href={profile.email ? `mailto:${profile.email}` : undefined}
              opacity={profile.email ? 1 : 0.5}
              pointerEvents={profile.email ? "auto" : "none"}
              gap={2}
              w={{ base: "full", md: "auto" }}
            >
              <Icon as={EmailIcon} boxSize="14px" />
              <Text as="span" noOfLines={1}>
                {profile.email || "No email"}
              </Text>
            </Flex>
          </Stack>

          <Box textAlign={{ base: "left", md: "right" }}>
            <Link
              fontSize="xs"
              fontWeight={600}
              color="#4A2C2A"
              textDecoration="none"
              _hover={{ textDecoration: "underline" }}
              onClick={() => onViewProfile?.(profile.id)}
            >
              View Full Profile
            </Link>
          </Box>
        </Stack>
      </Flex>
    </Box>
  );
}
